// Reports → Sessions & occupancy (F-ANL-03, spec/17 R-03): sessions, play time, average length, players and occupancy
// against the previous period; the **hour × weekday heatmap** of occupancy — rows are the weekday of the business day
// (Friday night's 01:00 is Friday's), columns the venue's clock hours from its cutoff — its peak, occupancy by clock
// hour, sessions each day against their average, and by station class and zone. Needs `reports:read` only: there is
// no money on it.

import type { SessionsReport } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { BarChart, ChartEmpty, ChartTable, ColumnChart, Heatmap, StatCard } from '@clavian/ui-web';
import type { ReactNode } from 'react';

import { hourWords, SessionsByDayCard, withDelta } from './OverviewTab';
import { KpiFigure, ReportCard, ReportGrid, ReportKpis, ReportNote, ReportPage } from './report-kit';
import { type ReportScope, useSessionsReport } from './queries';
import { deltaOf, ExportCsv, percentOf, sparkOf, TabEmpty, TabPending } from './tab-kit';

type Group = SessionsReport['byClass'][number];
type Cell = SessionsReport['heatmap'][number];

export function SessionsTab({ scope, previousLabel }: { readonly scope: ReportScope; readonly previousLabel: string }): ReactNode {
  const { t } = useTranslation('console');
  const report = useSessionsReport(scope, true);
  if (report.data === undefined) return <TabPending query={report} unavailable={t('reports.unavailable')} forbidden={t('reports.notPermitted')} />;
  if (report.data.totals.sessions === null && report.data.totals.availableSeconds === 0) return <TabEmpty title={t('reports.emptyTitle')} description={t('reports.sessions.empty')} />;
  return <Sessions report={report.data} previousLabel={previousLabel} />;
}

function Sessions({ report, previousLabel }: { readonly report: SessionsReport; readonly previousLabel: string }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const caption = t('reports.vsPrevious', { range: previousLabel });
  const now = report.totals;
  const before = report.previous;
  const weekday = (index: number, long: boolean) => t(long ? `reports.weekdayLong.${index}` : `reports.weekday.${index}`);
  const describeCell = (cell: { weekday: number; hour: number; value: number | null }) =>
    cell.value === null
      ? t('reports.sessions.cellClosed', { day: weekday(cell.weekday, true), hour: hourWords(format, cell.hour) })
      : t('reports.sessions.cell', { day: weekday(cell.weekday, true), hour: hourWords(format, cell.hour), occupancy: percentOf(format, cell.value) });
  const hours = Array.from({ length: 24 }, (_, index) => (report.firstHour + index) % 24);
  const byHour = hours.map((hour) => report.byHour.find((entry) => entry.hour === hour) ?? { hour, inSessionSeconds: 0, availableSeconds: 0, basisPoints: null });
  const peak = report.peaks[0];
  const peakWords = peak === undefined ? undefined : t('reports.sessions.peakMeta', { day: weekday(peak.weekday, true), hour: hourWords(format, peak.hour), occupancy: percentOf(format, peak.basisPoints) });
  const max = Math.max(0, ...report.heatmap.map((cell) => cell.basisPoints ?? 0));
  const open = byHour.filter((entry) => entry.basisPoints !== null);

  return (
    <ReportPage>
      {report.partial ? <ReportNote>{t('reports.partial')}</ReportNote> : null}
      <ReportKpis label={t('reports.tab.sessions')}>
        <StatCard
          label={t('reports.kpi.sessions')}
          value={<KpiFigure value={now.sessions} format={(n) => format.number(n)} />}
          {...withDelta(deltaOf(now.sessions, before.sessions, caption, format))}
          hint={now.activePlayers === null ? t('reports.noData') : t('reports.kpi.sessionsHint', { players: now.activePlayers, fresh: now.newPlayers ?? 0 })}
          {...sparkOf(report.byDay.map((day) => day.sessions))}
        />
        <StatCard
          label={t('reports.kpi.occupancy')}
          value={<KpiFigure value={now.occupancyBasisPoints} format={(bp) => percentOf(format, bp, 1)} />}
          {...withDelta(deltaOf(now.occupancyBasisPoints, before.occupancyBasisPoints, caption, format))}
          hint={report.hoursSet ? t('reports.kpi.occupancyHint') : t('reports.kpi.occupancyNoHours')}
        />
        <StatCard
          label={t('reports.kpi.averageSession')}
          value={<KpiFigure value={now.averageSeconds} format={(n) => format.duration(n)} />}
          {...withDelta(deltaOf(now.averageSeconds, before.averageSeconds, caption, format))}
          {...sparkOf(report.byDay.map((day) => (day.sessions === 0 ? null : Math.round(day.playSeconds / day.sessions))))}
        />
        <StatCard
          label={t('reports.column.playTime')}
          value={<KpiFigure value={now.playSeconds} format={(n) => format.duration(n)} />}
          {...withDelta(deltaOf(now.playSeconds, before.playSeconds, caption, format))}
          {...sparkOf(report.byDay.map((day) => day.playSeconds))}
        />
      </ReportKpis>

      <ReportCard title={t('reports.sessions.heatmap')} description={peakWords ?? t('reports.sessions.heatmapHint')}>
        {max === 0 ? (
          <ChartEmpty>{t('reports.sessions.noPeaks')}</ChartEmpty>
        ) : (
          <Heatmap
            label={t('reports.sessions.heatmap')}
            {...(peakWords === undefined ? {} : { summary: `${peakWords}. ${t('reports.sessions.heatmapHint')}` })}
            cells={report.heatmap.map((cell) => ({ weekday: cell.weekday, hour: cell.hour, value: cell.basisPoints }))}
            firstHour={report.firstHour}
            weekdayLabel={(index) => weekday(index, false)}
            hourLabel={(hour) => hourWords(format, hour)}
            describeCell={describeCell}
            max={max}
            formatValue={(value) => percentOf(format, value)}
            closedLabel={t('reports.sessions.legendClosed')}
          />
        )}
        <ChartTable
          caption={t('reports.sessions.heatmap')}
          rows={Array.from({ length: 7 }, (_, index) => index)}
          rowKey={(row) => String(row)}
          columns={[
            { key: 'day', header: t('reports.column.weekday'), cell: (row) => weekday(row, true) },
            ...hours.map((hour) => ({
              key: `h${hour}`,
              header: hourWords(format, hour),
              numeric: true,
              cell: (row: number) => {
                const cell = report.heatmap.find((entry: Cell) => entry.weekday === row && entry.hour === hour);
                return <span className="console-num">{percentOf(format, cell?.basisPoints ?? null)}</span>;
              },
            })),
          ]}
        />
        <div className="console-rt-actions">
          <ExportCsv
            fileName={`occupancy-${report.from}-${report.to}.csv`}
            header={['weekday', 'hour', 'in_session_seconds', 'available_seconds', 'occupancy_percent']}
            rows={report.heatmap.map((cell) => [weekday(cell.weekday, true), cell.hour, cell.inSessionSeconds, cell.availableSeconds, cell.basisPoints === null ? '' : (cell.basisPoints / 100).toFixed(2)])}
          />
        </div>
      </ReportCard>

      <ReportGrid layout="even">
        <ReportCard title={t('reports.sessions.byHour')} description={t('reports.sessions.byHourHint')}>
          {open.length === 0 ? (
            <ChartEmpty>{t('reports.sessions.noPeaks')}</ChartEmpty>
          ) : (
            <ColumnChart
              label={t('reports.sessions.byHour')}
              categories={byHour.map((entry) => hourWords(format, entry.hour))}
              categoryLabel={(index) => hourWords(format, byHour[index]?.hour ?? 0)}
              series={[{ key: 'occupancy', label: t('reports.kpi.occupancy'), tone: 1, values: byHour.map((entry) => entry.basisPoints ?? 0) }]}
              format={(value) => percentOf(format, value)}
              {...(now.occupancyBasisPoints === null ? {} : { reference: { value: now.occupancyBasisPoints, label: t('reports.sessions.average', { value: percentOf(format, now.occupancyBasisPoints) }) } })}
            />
          )}
          <ChartTable
            caption={t('reports.sessions.byHour')}
            rows={byHour}
            rowKey={(row) => String(row.hour)}
            columns={[
              { key: 'hour', header: t('reports.column.hour'), cell: (row) => hourWords(format, row.hour) },
              { key: 'occupancy', header: t('reports.kpi.occupancy'), numeric: true, cell: (row) => <span className="console-num">{percentOf(format, row.basisPoints)}</span> },
            ]}
          />
        </ReportCard>
        <SessionsByDayCard sessions={report} />
      </ReportGrid>

      <ReportGrid layout="even">
        <GroupCard title={t('reports.sessions.byClass')} groups={report.byClass} empty={t('reports.sessions.noClasses')} />
        <GroupCard title={t('reports.sessions.byZone')} groups={report.byZone} empty={t('reports.sessions.noZones')} />
      </ReportGrid>
    </ReportPage>
  );
}

/** Occupancy per station class or zone: a bar on the 0–100 % scale, the percentage and its stations. */
function GroupCard({ title, groups, empty }: { readonly title: string; readonly groups: readonly Group[]; readonly empty: string }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const sorted = [...groups].sort((a, b) => (b.basisPoints ?? -1) - (a.basisPoints ?? -1));
  return (
    <ReportCard title={title} description={t('reports.sessions.groupHint')}>
      {groups.length === 0 ? (
        <ChartEmpty>{empty}</ChartEmpty>
      ) : (
        <BarChart
          label={title}
          max={10_000}
          rows={sorted.map((group) => ({
            key: group.id ?? 'none',
            label: group.name ?? t('reports.sessions.noZone'),
            value: group.basisPoints ?? 0,
            display: percentOf(format, group.basisPoints),
            meta: t('reports.sessions.stations', { count: group.stations }),
          }))}
        />
      )}
    </ReportCard>
  );
}
